import { FormProps } from "@aircall/tractor";
import { useGraphMutation, useToast } from "@dashboard/library";
import { useTranslation } from "react-i18next";

import { useTrackHelpers } from "@/hooks/useTrackHelpers/useTrackHelpers";

import {
  CreateNoteMutation,
  CreateNoteMutationVariables,
  NoteAgentDetail,
} from "@/graphql/generated/graphql";
import { CreateNote } from "@/graphql/mutations/CreateNote";
import { GetTagsAndNotes } from "@/graphql/queries/GetTagsAndNotes";
import { useCurrentCompany } from "@/hooks/useCurrentCompany/useCurrentCompany";
import { useCurrentUser } from "@/hooks/useCurrentUser/useCurrentUser";
import { useTrack } from "@/hooks/useTrack/useTrack";
import { usePathParams } from "@/states/path_param";

export function useCreateNote() {
  const { t } = useTranslation();
  const { callId } = usePathParams();

  const { show } = useToast();
  const { currentUser } = useCurrentUser();
  const { currentCompany } = useCurrentCompany();
  const { track } = useTrack();

  const {
    transcription_displayed,
    summary_displayed,
    key_topics_displayed,
    talk_ratio_displayed,
  } = useTrackHelpers();

  const [createNote, { loading }] = useGraphMutation<
    CreateNoteMutation,
    CreateNoteMutationVariables
  >(CreateNote, {
    onCompleted({ createNote: { content } }) {
      show({
        message: t("note_saved"),
        variant: "success",
      });
      track("conversation_note_added", {
        call_id: callId,
        character_count: content.length,
        key_topics_displayed,
        talk_ratio_displayed,
        transcription_displayed,
        summary_displayed,
      });
    },
    onError(e) {
      show({
        variant: "critical",
        message: e.message,
      });
    },
  });

  const handleOnSubmit: FormProps["onSubmit"] = (e) => {
    const formData = new FormData(e.currentTarget);
    const content = formData.get("content");

    if (!content || typeof content !== "string") {
      return;
    }

    const optimisticNote: CreateNoteMutation = {
      createNote: {
        id: "temp-id",
        userId: Number(currentUser?.ID),
        callId: Number(callId),
        createdAt: new Date().toISOString(),
        content,
      },
    };

    createNote({
      variables: {
        callId,
        content,
      },
      optimisticResponse: optimisticNote,
      update(cache, { data }) {
        if (!data) {
          return;
        }

        const readTagsAndNotes = cache.readQuery({
          query: GetTagsAndNotes,
          variables: {
            callId,
          },
        });

        const builtOptimisticNote: NoteAgentDetail = {
          id: data.createNote.id,
          callId: data.createNote.callId,
          userId: data.createNote.userId,
          content: data.createNote.content,
          createdAt: data.createNote.createdAt,
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
          companyId: Number(currentCompany?.id),
        };

        cache.writeQuery({
          query: GetTagsAndNotes,
          data: {
            getTagsAndNotes: {
              __typename: "TagsAndNotes",
              tags: readTagsAndNotes?.getTagsAndNotes?.tags ?? [],
              notes: [
                builtOptimisticNote,
                ...(readTagsAndNotes?.getTagsAndNotes?.notes ?? []),
              ],
            },
          },
          variables: {
            callId,
          },
        });
      },
    });
  };

  return {
    handleOnSubmit,
    loading,
  };
}
